import {CommonModule} from '@angular/common';
import {Component, computed, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import * as XLSX from 'xlsx';
import {
    DownloadXmlCompraService,
    XmlCompraItemBusca,
    XmlCompraNota
} from '../../services/download-xml-compra.service';

interface CampoXml { rotulo: string; valor: string; }
interface ItemXml {
    item: string; codigo: string; descricao: string; ncm: string; cfop: string; un: string;
    qtde: number; vlrUnit: number; vlrTotal: number; icms: number; ipi: number;
}
interface PreviewXml {
    arquivo: string;
    avisos: string[];
    capa: CampoXml[];
    emitente: CampoXml[];
    destinatario: CampoXml[];
    totais: CampoXml[];
    itens: ItemXml[];
}

/**
 * Tela "Download de XML de Compra" (menu Fiscal): le uma planilha com chave de acesso e/ou
 * Num. NF, busca as notas de ENTRADA no Aerosys (FISCAL_NFE, NFE_ES = 'E') e baixa o NFE_XML
 * ja como arquivo .xml (um a um ou em ZIP). Tambem testa/pre-visualiza um XML (capa e itens).
 */
@Component({
    selector: 'app-download-xml-compra',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './download-xml-compra.component.html',
    styleUrl: './download-xml-compra.component.css'
})
export class DownloadXmlCompraComponent {
    // ── 1. Arquivo ──────────────────────────────────────────────
    nomeArquivo = '';
    primeiraLinhaCabecalho = true;
    private linhasBrutas: string[][] = [];
    readonly colunas = signal<string[]>([]);
    colChave = -1;
    colNumNf = -1;
    readonly previaLinhas = signal<string[][]>([]);
    readonly erroArquivo = signal('');

    // ── 2. Resultado ────────────────────────────────────────────
    readonly buscando = signal(false);
    readonly erroBusca = signal('');
    readonly notas = signal<XmlCompraNota[]>([]);
    readonly naoEncontrados = signal<XmlCompraItemBusca[]>([]);
    readonly buscou = signal(false);
    readonly selecionadas = signal<Set<number>>(new Set());
    readonly baixando = signal(false);
    readonly mensagem = signal('');
    mostrarNaoEncontrados = false;
    separarPorMes = true;

    readonly qtdSelecionadas = computed(() => this.selecionadas().size);
    readonly notasComXml = computed(() => this.notas().filter(n => n.temXml));

    // ── 3. Testar XML ───────────────────────────────────────────
    readonly preview = signal<PreviewXml | null>(null);
    readonly erroPreview = signal('');
    readonly carregandoPreview = signal(false);

    constructor(private readonly service: DownloadXmlCompraService) {}

    // ═════════════ Arquivo ═════════════
    onArquivo(event: Event): void {
        const input = event.target as HTMLInputElement;
        const arquivo = input.files?.[0];
        input.value = '';
        if (!arquivo) return;
        this.erroArquivo.set('');
        this.nomeArquivo = arquivo.name;
        const leitor = new FileReader();
        leitor.onload = () => {
            try {
                const wb = XLSX.read(leitor.result as ArrayBuffer, {type: 'array', raw: true, cellText: true});
                const ws = wb.Sheets[wb.SheetNames[0]];
                // raw:false => usa o texto formatado da celula (evita notacao cientifica nas chaves).
                const linhas: any[][] = XLSX.utils.sheet_to_json(ws, {header: 1, defval: '', raw: false});
                this.linhasBrutas = linhas
                    .map(l => l.map(c => String(c ?? '').trim()))
                    .filter(l => l.some(c => c !== ''));
                if (this.linhasBrutas.length === 0) {
                    this.erroArquivo.set('O arquivo está vazio.');
                    return;
                }
                this.montarColunas();
                this.detectarColunas();
                this.limparResultado();
            } catch {
                this.erroArquivo.set('Não foi possível ler o arquivo. Use .xlsx, .xls ou .csv.');
            }
        };
        leitor.readAsArrayBuffer(arquivo);
    }

    onCabecalhoChange(): void {
        if (this.linhasBrutas.length === 0) return;
        this.montarColunas();
        this.detectarColunas();
    }

    private letra(i: number): string {
        let s = '';
        let n = i;
        do {
            s = String.fromCharCode(65 + (n % 26)) + s;
            n = Math.floor(n / 26) - 1;
        } while (n >= 0);
        return s;
    }

    private montarColunas(): void {
        const total = Math.max(...this.linhasBrutas.map(l => l.length));
        const cab = this.primeiraLinhaCabecalho ? this.linhasBrutas[0] : [];
        const nomes: string[] = [];
        for (let i = 0; i < total; i++) {
            const nome = (cab[i] || '').trim();
            nomes.push(nome ? `${nome} (${this.letra(i)})` : `Coluna ${this.letra(i)}`);
        }
        this.colunas.set(nomes);
        this.previaLinhas.set(this.dados().slice(0, 5));
    }

    private dados(): string[][] {
        return this.primeiraLinhaCabecalho ? this.linhasBrutas.slice(1) : this.linhasBrutas;
    }

    /** Sugere as colunas: chave = coluna com valores de 44 digitos; Num. NF = cabecalho com "NF"/"NOTA"/"NUM". */
    private detectarColunas(): void {
        this.colChave = -1;
        this.colNumNf = -1;
        const dados = this.dados().slice(0, 30);
        const total = this.colunas().length;
        for (let c = 0; c < total; c++) {
            const hits = dados.filter(l => (l[c] || '').replace(/\D/g, '').length === 44).length;
            if (dados.length && hits >= Math.ceil(dados.length / 2)) { this.colChave = c; break; }
        }
        if (this.primeiraLinhaCabecalho) {
            const cab = this.linhasBrutas[0].map(x => x.toLowerCase());
            if (this.colChave < 0) {
                const i = cab.findIndex(x => x.includes('chave'));
                if (i >= 0) this.colChave = i;
            }
            const j = cab.findIndex((x, i) => i !== this.colChave && /(num|n[uú]mero|nota|\bnf\b|nfe)/.test(x) && !x.includes('chave'));
            if (j >= 0) this.colNumNf = j;
        }
    }

    get podeBuscar(): boolean {
        return !this.buscando() && this.dados().length > 0 && (this.colChave >= 0 || this.colNumNf >= 0);
    }

    get totalLinhas(): number {
        return this.dados().length;
    }

    // ═════════════ Busca ═════════════
    buscar(): void {
        if (!this.podeBuscar) return;
        this.erroBusca.set('');
        this.mensagem.set('');
        const vistos = new Set<string>();
        const itens: XmlCompraItemBusca[] = [];
        let chavesInvalidas = 0;
        for (const l of this.dados()) {
            const chave = this.colChave >= 0 ? (l[this.colChave] || '').replace(/\D/g, '') : '';
            const numNf = this.colNumNf >= 0 ? (l[this.colNumNf] || '').trim() : '';
            if (chave && chave.length !== 44) chavesInvalidas++;
            if (!chave && !numNf) continue;
            const id = `${chave}|${numNf}`;
            if (vistos.has(id)) continue;
            vistos.add(id);
            itens.push({chave: chave.length === 44 ? chave : '', numNf});
        }
        if (itens.length === 0) {
            this.erroBusca.set('Nenhuma linha com chave ou Num. NF nas colunas escolhidas.');
            return;
        }
        this.buscando.set(true);
        this.notas.set([]);
        this.naoEncontrados.set([]);
        this.selecionadas.set(new Set());
        // Lotes de 200 linhas por chamada.
        const lotes: XmlCompraItemBusca[][] = [];
        for (let i = 0; i < itens.length; i += 200) lotes.push(itens.slice(i, i + 200));
        const acumuladas = new Map<number, XmlCompraNota>();
        const faltantes: XmlCompraItemBusca[] = [];
        const proximo = (idx: number): void => {
            if (idx >= lotes.length) {
                this.notas.set([...acumuladas.values()]);
                this.naoEncontrados.set(faltantes);
                this.buscou.set(true);
                this.buscando.set(false);
                if (chavesInvalidas > 0) {
                    this.mensagem.set(`${chavesInvalidas} linha(s) com chave que não tem 44 dígitos — conferiu se a coluna não virou notação científica no Excel?`);
                }
                return;
            }
            this.service.buscar(lotes[idx]).subscribe({
                next: r => {
                    r.notas.forEach(n => acumuladas.set(n.nfeId, n));
                    r.naoEncontrados.forEach(n => faltantes.push(n));
                    proximo(idx + 1);
                },
                error: e => {
                    this.erroBusca.set(e?.error?.detail || e?.error?.message || 'Não foi possível consultar o Aerosys.');
                    this.buscando.set(false);
                }
            });
        };
        proximo(0);
    }

    limparResultado(): void {
        this.notas.set([]);
        this.naoEncontrados.set([]);
        this.selecionadas.set(new Set());
        this.buscou.set(false);
        this.erroBusca.set('');
        this.mensagem.set('');
    }

    // ═════════════ Seleção / download ═════════════
    selecionada(n: XmlCompraNota): boolean {
        return this.selecionadas().has(n.nfeId);
    }

    alternar(n: XmlCompraNota): void {
        const s = new Set(this.selecionadas());
        if (s.has(n.nfeId)) s.delete(n.nfeId); else s.add(n.nfeId);
        this.selecionadas.set(s);
    }

    get todasSelecionadas(): boolean {
        const com = this.notasComXml();
        return com.length > 0 && com.every(n => this.selecionadas().has(n.nfeId));
    }

    alternarTodas(): void {
        this.selecionadas.set(this.todasSelecionadas ? new Set() : new Set(this.notasComXml().map(n => n.nfeId)));
    }

    baixarUma(n: XmlCompraNota): void {
        if (this.baixando()) return;
        this.baixando.set(true);
        this.erroBusca.set('');
        this.service.xml(n.nfeId).subscribe({
            next: blob => { this.salvar(blob, this.nomeXml(n)); this.baixando.set(false); },
            error: e => this.falhaDownload(e)
        });
    }

    baixarZip(todas: boolean): void {
        const ids = todas ? this.notasComXml().map(n => n.nfeId) : [...this.selecionadas()];
        if (ids.length === 0 || this.baixando()) return;
        this.baixando.set(true);
        this.erroBusca.set('');
        this.service.zip(ids, this.separarPorMes).subscribe({
            next: blob => { this.salvar(blob, 'xml_compras.zip'); this.baixando.set(false); },
            error: e => this.falhaDownload(e)
        });
    }

    private falhaDownload(_e?: unknown): void {
        this.baixando.set(false);
        this.erroBusca.set('Não foi possível baixar o XML (a nota pode estar sem XML gravado).');
    }

    private nomeXml(n: XmlCompraNota): string {
        const base = n.chave?.trim() || `NFe_${n.numNota}_${n.nfeId}`;
        return base.replace(/[^A-Za-z0-9_.-]/g, '_') + '.xml';
    }

    private salvar(blob: Blob, nome: string): void {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = nome;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
    }

    // ═════════════ Testar XML ═════════════
    testarNota(n: XmlCompraNota): void {
        this.erroPreview.set('');
        this.carregandoPreview.set(true);
        this.service.xml(n.nfeId).subscribe({
            next: async blob => {
                this.interpretar(await blob.text(), this.nomeXml(n));
                this.carregandoPreview.set(false);
            },
            error: () => {
                this.erroPreview.set('Não foi possível carregar o XML desta nota.');
                this.carregandoPreview.set(false);
            }
        });
    }

    onArquivoXml(event: Event): void {
        const input = event.target as HTMLInputElement;
        const arquivo = input.files?.[0];
        input.value = '';
        if (!arquivo) return;
        this.erroPreview.set('');
        arquivo.text().then(t => this.interpretar(t, arquivo.name));
    }

    fecharPreview(): void {
        this.preview.set(null);
        this.erroPreview.set('');
    }

    private interpretar(texto: string, nome: string): void {
        const doc = new DOMParser().parseFromString(texto, 'application/xml');
        if (doc.getElementsByTagName('parsererror').length > 0) {
            this.preview.set(null);
            this.erroPreview.set('XML inválido: não foi possível interpretar o arquivo.');
            return;
        }
        const el = (pai: Element | Document | null, tag: string): Element | null =>
            pai ? (pai.getElementsByTagNameNS('*', tag)[0] ?? null) : null;
        const tx = (pai: Element | Document | null, tag: string): string => (el(pai, tag)?.textContent ?? '').trim();
        const num = (v: string): number => parseFloat(v.replace(',', '.')) || 0;
        const campo = (rotulo: string, valor: string): CampoXml => ({rotulo, valor: valor || '—'});

        const avisos: string[] = [];
        const infNFe = el(doc, 'infNFe');
        if (!infNFe) {
            this.preview.set(null);
            this.erroPreview.set('Este XML não é de uma NF-e (elemento infNFe não encontrado).');
            return;
        }
        if (!el(doc, 'protNFe')) avisos.push('XML sem protocolo de autorização (protNFe) — a importação em alguns sistemas exige nfeProc.');
        const chave = (infNFe.getAttribute('Id') || '').replace(/^NFe/, '') || tx(doc, 'chNFe');
        if (chave.length !== 44) avisos.push('Chave de acesso inválida (não tem 44 dígitos).');

        const ide = el(infNFe, 'ide');
        const emit = el(infNFe, 'emit');
        const dest = el(infNFe, 'dest');
        const tot = el(infNFe, 'ICMSTot');
        const fone = (pai: Element | null) => tx(pai, 'xMun') ? `${tx(pai, 'xMun')}/${tx(pai, 'UF')}` : '';
        const data = (v: string) => {
            if (!v) return '';
            const d = new Date(v);
            return isNaN(d.getTime()) ? v : d.toLocaleString('pt-BR');
        };
        const moeda = (v: string) => v ? num(v).toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '';

        const itens: ItemXml[] = [];
        Array.from(infNFe.getElementsByTagNameNS('*', 'det')).forEach(det => {
            const prod = el(det, 'prod');
            const imp = el(det, 'imposto');
            itens.push({
                item: det.getAttribute('nItem') || '',
                codigo: tx(prod, 'cProd'),
                descricao: tx(prod, 'xProd'),
                ncm: tx(prod, 'NCM'),
                cfop: tx(prod, 'CFOP'),
                un: tx(prod, 'uCom'),
                qtde: num(tx(prod, 'qCom')),
                vlrUnit: num(tx(prod, 'vUnCom')),
                vlrTotal: num(tx(prod, 'vProd')),
                icms: num(tx(el(imp, 'ICMS'), 'vICMS')),
                ipi: num(tx(el(imp, 'IPI'), 'vIPI'))
            });
        });
        if (itens.length === 0) avisos.push('Nenhum item (det) encontrado no XML.');
        const somaItens = itens.reduce((s, i) => s + i.vlrTotal, 0);
        const vProdTotal = num(tx(tot, 'vProd'));
        if (itens.length && Math.abs(somaItens - vProdTotal) > 0.05) {
            avisos.push(`Soma dos itens (${somaItens.toFixed(2)}) difere do total de produtos (${vProdTotal.toFixed(2)}).`);
        }

        this.preview.set({
            arquivo: nome,
            avisos,
            capa: [
                campo('Chave de acesso', chave),
                campo('Nº NF', tx(ide, 'nNF')),
                campo('Série', tx(ide, 'serie')),
                campo('Modelo', tx(ide, 'mod')),
                campo('Natureza da operação', tx(ide, 'natOp')),
                campo('Emissão', data(tx(ide, 'dhEmi') || tx(ide, 'dEmi'))),
                campo('Tipo (0=Entrada, 1=Saída)', tx(ide, 'tpNF')),
                campo('Protocolo', tx(doc, 'nProt')),
                campo('Modalidade do frete', tx(el(infNFe, 'transp'), 'modFrete'))
            ],
            emitente: [
                campo('Razão social', tx(emit, 'xNome')),
                campo('CNPJ', tx(emit, 'CNPJ') || tx(emit, 'CPF')),
                campo('IE', tx(emit, 'IE')),
                campo('Cidade/UF', fone(emit))
            ],
            destinatario: [
                campo('Razão social', tx(dest, 'xNome')),
                campo('CNPJ/CPF', tx(dest, 'CNPJ') || tx(dest, 'CPF')),
                campo('IE', tx(dest, 'IE')),
                campo('Cidade/UF', fone(dest))
            ],
            totais: [
                campo('Base ICMS', moeda(tx(tot, 'vBC'))),
                campo('Valor ICMS', moeda(tx(tot, 'vICMS'))),
                campo('Valor produtos', moeda(tx(tot, 'vProd'))),
                campo('Frete', moeda(tx(tot, 'vFrete'))),
                campo('Seguro', moeda(tx(tot, 'vSeg'))),
                campo('Desconto', moeda(tx(tot, 'vDesc'))),
                campo('IPI', moeda(tx(tot, 'vIPI'))),
                campo('Outras despesas', moeda(tx(tot, 'vOutro'))),
                campo('Valor da NF', moeda(tx(tot, 'vNF')))
            ],
            itens
        });
    }
}
